/**
 * Alerts card on the Activity page. Lists configured alerts with a
 * one-line summary; admins can add/edit/delete, caregivers see
 * read-only.
 */

import { useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { useAlerts, type AlertWithId } from '@/hooks/useAlerts';
import { useMembers } from '@/hooks/useMembers';
import { deleteAlert } from '@/lib/firestore';
import { AlertFormDialog } from './AlertFormDialog';

interface Props {
  aid: string;
  isAdmin: boolean;
}

export function AlertsCard({ aid, isAdmin }: Props) {
  const { alerts, loading } = useAlerts(aid);
  const { members } = useMembers(aid);
  const [editing, setEditing] = useState<AlertWithId | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  function openCreate() {
    setEditing(null);
    setDialogOpen(true);
  }
  function openEdit(a: AlertWithId) {
    setEditing(a);
    setDialogOpen(true);
  }

  async function confirmDelete(alertId: string) {
    setDeleteError(null);
    try {
      await deleteAlert(aid, alertId);
      setConfirmingDelete(null);
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Delete failed.');
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Alerts</CardTitle>
          {isAdmin && (
            <Button size="sm" onClick={openCreate}>
              + Add alert
            </Button>
          )}
        </div>
      </CardHeader>
      <CardBody>
        {loading ? (
          <div className="flex justify-center py-4">
            <Spinner />
          </div>
        ) : alerts.length === 0 ? (
          <p className="text-sm text-ink-soft">
            {isAdmin
              ? 'No alerts yet. Add one to be emailed if there are no button presses by a given time.'
              : "No alerts configured. Ask an admin to set one up."}
          </p>
        ) : (
          <ul className="divide-y divide-paper-border">
            {alerts.map((a) => {
              // Resolve each configured UID to a display label. UIDs
              // for caregivers who've since been removed surface as
              // "(removed)" so the admin notices an alert with no live
              // recipients (the dispatcher silently skips those).
              const recipientLabels = a.recipientUids.map((uid) => {
                const m = members.find((mem) => mem.id === uid);
                if (!m) return '(removed)';
                return m.displayName || m.email || m.id;
              });
              const hasRemoved = recipientLabels.includes('(removed)');
              return (
                <li key={a.id} className="flex items-start justify-between gap-3 py-3">
                  <div className="flex-1">
                    <p className="text-sm text-ink">
                      <span className="font-medium">{formatHour(a.hourOfDay)}</span>
                      {' — alert if no button press in last '}
                      <span className="font-medium">
                        {a.lookbackHours} {a.lookbackHours === 1 ? 'hour' : 'hours'}
                      </span>
                      {!a.enabled && <span className="ml-2 text-ink-muted">(disabled)</span>}
                    </p>
                    <p className="mt-1 text-xs text-ink-soft">
                      Email{recipientLabels.length === 1 ? '' : 's'}:{' '}
                      {recipientLabels.length > 0 ? recipientLabels.join(', ') : '(none picked)'}
                    </p>
                    {hasRemoved && (
                      <p className="mt-1 text-xs text-amber-700">
                        One or more recipients have been removed from this account — they
                        won't receive emails. Edit the alert to update the list.
                      </p>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="flex shrink-0 gap-2">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(a)}>
                        Edit
                      </Button>
                      {confirmingDelete === a.id ? (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setConfirmingDelete(null)}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => void confirmDelete(a.id)}
                          >
                            Delete
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-700 hover:bg-red-50"
                          onClick={() => setConfirmingDelete(a.id)}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {deleteError && (
          <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {deleteError}
          </div>
        )}
      </CardBody>
      {isAdmin && (
        <AlertFormDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          aid={aid}
          initial={editing}
        />
      )}
    </Card>
  );
}

function formatHour(h: number): string {
  if (h === 0) return '12 AM';
  if (h < 12) return `${h} AM`;
  if (h === 12) return '12 PM';
  return `${h - 12} PM`;
}
